// ============================================================
//  AI 评语「每日额度」展示状态纯逻辑（无框架、无副作用）
//  契约：.scratch/ai-comment-daily-limit（spec 第六/八章）
//    GET /api/aiComment/Quota → { dailyLimit, usedToday, remaining }
//      · 有限：dailyLimit > 0，remaining = dailyLimit − usedToday（≥ 0）
//      · 不限：dailyLimit = 0、usedToday = 0、remaining = -1（前端隐藏额度 UI）
//  本文件是全项目额度展示的唯一行为缝隙：页面只消费这里的状态，
//  将来补自动化测试的唯一目标即本文件。
// ============================================================

/** 额度快照（与后端 Quota / Generate 响应对齐，宽松数值语义） */
export interface AiCommentQuota {
  /** 解析后的每日限额：> 0 有限；0 或负数 = 不限 */
  dailyLimit: number;
  /** 今日已成功生成次数 */
  usedToday: number;
  /** 剩余可生成次数：≥ 0 有限；< 0（-1）= 不限 */
  remaining: number;
}

/** 额度展示状态：unlimited=不限（隐藏额度 UI）/ exhausted=已用完（置灰+专门文案）/ ok=有剩余（今日剩余 X/N） */
export type AiCommentQuotaView =
  | { state: "unlimited" }
  | { state: "exhausted"; remainingText: string; message: string }
  | { state: "ok"; remainingText: string };

/** 宽松解析后端 Quota 响应 → 额度快照；形状不对返回 null（= 未知，不展示不拦截） */
export function parseQuota(raw: unknown): AiCommentQuota | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const dailyLimit = Number(o["dailyLimit"]);
  const usedToday = Number(o["usedToday"]);
  const remaining = Number(o["remaining"]);
  if (![dailyLimit, usedToday, remaining].every(Number.isFinite)) return null;
  return { dailyLimit, usedToday, remaining };
}

/** 「今日生成次数已用完（N 次），请明天再试」—— N = 每日限额（与后端 429 文案一致） */
export function quotaExhaustedMessage(dailyLimit: number): string {
  return `今日生成次数已用完（${dailyLimit} 次），请明天再试`;
}

/** 是否「不限」：限额配置 0/负数，或后端以 remaining < 0 表达。不限时不展示额度、不因额度拦截/置灰 */
export function quotaIsUnlimited(quota: AiCommentQuota): boolean {
  return quota.dailyLimit <= 0 || quota.remaining < 0;
}

/**
 * 额度展示状态归一化（唯一规则）：
 *  ① quotaIsUnlimited → unlimited（管理员配置 0/负数 = 不限）
 *  ② remaining == 0 → exhausted（置灰 + 专门文案）
 *  ③ 其余 → ok，文案「今日剩余 X/N」
 */
export function resolveQuotaView(quota: AiCommentQuota): AiCommentQuotaView {
  const { dailyLimit, remaining } = quota;
  if (quotaIsUnlimited(quota)) return { state: "unlimited" };
  const remainingText = `今日剩余 ${remaining}/${dailyLimit}`;
  if (remaining === 0) {
    return {
      state: "exhausted",
      remainingText,
      message: quotaExhaustedMessage(dailyLimit),
    };
  }
  return { state: "ok", remainingText };
}
